import {
  getOnboardingStepRoute,
  OnboardingStep,
  type OnboardingType,
} from "@/enums/route";
import {
  FLOW_OF_FUNDS_MIN_LENGTH,
  isAddressComplete,
  isBusinessDescriptionComplete,
  isFilled,
  isNonEmptyList,
  isPositiveAmount,
  meetsMinLength,
  OTHER_PURPOSE_MIN_LENGTH,
} from "@/utils/onboardingFieldRules";
import {
  BusinessType,
  PrimaryContactRelationship,
} from "@generated/redux/services/generated/api";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type StepStatus = "completed" | "current" | "upcoming";

export interface StepMeta {
  step: OnboardingStep;
  status: StepStatus;
  isComplete: boolean;
  isAccessible: boolean;
  labelKey: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FormData = Record<string, any> | undefined;

// ---------------------------------------------------------------------------
// Step order arrays
// ---------------------------------------------------------------------------

export const PERSONAL_STEP_ORDER: OnboardingStep[] = [
  OnboardingStep.ContactDetails,
  OnboardingStep.HomeAddress,
  OnboardingStep.AccountUse,
  OnboardingStep.IncomingPayments,
  OnboardingStep.OutgoingPayments,
  OnboardingStep.Terms,
  OnboardingStep.Review,
];

export const BUSINESS_STEP_ORDER: OnboardingStep[] = [
  OnboardingStep.Type,
  OnboardingStep.CompanyDetails,
  OnboardingStep.CompanyAddress,
  OnboardingStep.ContactDetails,
  OnboardingStep.AccountUse,
  OnboardingStep.IncomingPayments,
  OnboardingStep.OutgoingPayments,
  OnboardingStep.Terms,
  OnboardingStep.Review,
];

// ---------------------------------------------------------------------------
// Step label keys (translation key for each step name)
// ---------------------------------------------------------------------------

export const STEP_LABEL_KEYS: Record<string, string> = {
  [OnboardingStep.Type]: "steps.type",
  [OnboardingStep.CompanyDetails]: "steps.companyDetails",
  [OnboardingStep.CompanyAddress]: "steps.companyAddress",
  [OnboardingStep.ContactDetails]: "steps.contactDetails",
  [OnboardingStep.HomeAddress]: "steps.homeAddress",
  [OnboardingStep.AccountUse]: "steps.accountUse",
  [OnboardingStep.IncomingPayments]: "steps.incomingPayments",
  [OnboardingStep.OutgoingPayments]: "steps.outgoingPayments",
  [OnboardingStep.Terms]: "steps.terms",
  [OnboardingStep.Review]: "steps.review",
};

// ---------------------------------------------------------------------------
// Shared completion checks
// ---------------------------------------------------------------------------

/**
 * Every predicate below mirrors the matching page's `validateForm`, field for
 * field. Keep them in lockstep: a check that is looser here does NOT relax the
 * Save button, it silently marks a half-filled step "completed" — which makes
 * `getResumeStep` skip it and lets `Review` submit the gap. That is exactly how
 * an application reached submission with a blank `annualRevenue`.
 */

function isAccountUseComplete(formData: FormData): boolean {
  const au = formData?.accountUse;
  if (!au) return false;
  return (
    isFilled(au.baseCurrency) &&
    au.monthlyTurnover != null &&
    isNonEmptyList(au.accountPurpose?.selected) &&
    meetsMinLength(au.accountPurpose?.comment, OTHER_PURPOSE_MIN_LENGTH) &&
    meetsMinLength(au.flowOfFunds, FLOW_OF_FUNDS_MIN_LENGTH)
  );
}

function isIncomingPaymentsComplete(formData: FormData): boolean {
  const ip = formData?.incomingPayments;
  if (!ip) return false;
  return (
    isNonEmptyList(ip.sourceOfFunds?.selected) &&
    ip.expectedCreditsPerMonth != null &&
    ip.maxCreditValue != null &&
    isNonEmptyList(ip.sourceCountries) &&
    isNonEmptyList(ip.sourceCurrencies)
  );
}

function isOutgoingPaymentsComplete(formData: FormData): boolean {
  const op = formData?.outgoingPayments;
  if (!op) return false;
  return (
    op.expectedDebitsPerMonth != null &&
    op.maxDebitValue != null &&
    isNonEmptyList(op.destinationCountries) &&
    isNonEmptyList(op.destinationCurrencies)
  );
}

// ---------------------------------------------------------------------------
// Personal step completion
// ---------------------------------------------------------------------------

function isPersonalContactDetailsComplete(formData: FormData): boolean {
  const pd = formData?.personalDetails;
  if (!pd) return false;
  return (
    isFilled(pd.title) &&
    isFilled(pd.dateOfBirth) &&
    isNonEmptyList(pd.nationalities) &&
    isFilled(pd.occupation)
  );
}

function isPersonalHomeAddressComplete(formData: FormData): boolean {
  return isAddressComplete(formData?.personalDetails?.address);
}

/**
 * Check whether a single personal-flow step is complete based on signal
 * fields in the application formData.
 */
export function isPersonalStepComplete(
  step: OnboardingStep,
  formData: FormData
): boolean {
  switch (step) {
    case OnboardingStep.ContactDetails:
      return isPersonalContactDetailsComplete(formData);
    case OnboardingStep.HomeAddress:
      return isPersonalHomeAddressComplete(formData);
    case OnboardingStep.AccountUse:
      return isAccountUseComplete(formData);
    case OnboardingStep.IncomingPayments:
      return isIncomingPaymentsComplete(formData);
    case OnboardingStep.OutgoingPayments:
      return isOutgoingPaymentsComplete(formData);
    case OnboardingStep.Terms:
      return Boolean(formData?.consents?.privacyAccepted);
    case OnboardingStep.Review:
      // Review is "complete" only when all prior steps are done
      return PERSONAL_STEP_ORDER.slice(0, -1).every((s) =>
        isPersonalStepComplete(s, formData)
      );
    default:
      return false;
  }
}

// ---------------------------------------------------------------------------
// Business step completion
// ---------------------------------------------------------------------------

function isBusinessTypeComplete(formData: FormData): boolean {
  const businessType = formData?.businessDetails?.businessType;
  if (!isFilled(businessType?.selected)) return false;
  // "Other" requires the free-text comment that names the actual type.
  if (businessType.selected === BusinessType.Other) {
    return isFilled(businessType.comment);
  }
  return true;
}

function isBusinessCompanyDetailsComplete(formData: FormData): boolean {
  const bd = formData?.businessDetails;
  if (!bd) return false;

  // Charities and "Other" have no registration number — the form hides the
  // input for them, so requiring it here would deadlock those applicants.
  const selectedType = bd.businessType?.selected;
  const requiresCompanyNumber =
    selectedType !== BusinessType.Charity &&
    selectedType !== BusinessType.Other;

  return (
    isFilled(bd.companyName) &&
    (!requiresCompanyNumber || isFilled(bd.companyNumber)) &&
    isFilled(bd.countryOfIncorporation) &&
    isFilled(bd.industrySector) &&
    isPositiveAmount(bd.annualRevenue) &&
    // Collected on this step but persisted under accountUse.
    isFilled(formData?.accountUse?.baseCurrency) &&
    isBusinessDescriptionComplete(bd.businessDescription)
  );
  // `companyWebsite` is intentionally absent — it renders with an "(optional)"
  // suffix and the form does not validate it.
}

function isBusinessCompanyAddressComplete(formData: FormData): boolean {
  const addresses = formData?.businessDetails?.addresses;
  if (!isAddressComplete(addresses?.registered)) return false;
  // The form saves `trading` as a copy of `registered` when "same as
  // registered" is ticked, so a complete trading address is always expected.
  return isAddressComplete(addresses?.trading);
}

function isBusinessContactDetailsComplete(formData: FormData): boolean {
  const pc = formData?.businessDetails?.primaryContact;
  if (!pc) return false;

  // The relationship/position is required in BOTH branches of the form.
  const relationship = pc.relationship;
  if (!isFilled(relationship?.selected)) return false;
  if (
    relationship.selected === PrimaryContactRelationship.Other &&
    !isFilled(relationship.comment)
  ) {
    return false;
  }

  // When isApplicant=true, the applicant IS the primary contact — the form
  // deliberately sends no contactDetails.
  if (pc.isApplicant) return true;

  const cd = pc.contactDetails;
  if (!cd) return false;
  return (
    isFilled(cd.title) &&
    isFilled(cd.firstName) &&
    isFilled(cd.lastName) &&
    isFilled(cd.dateOfBirth) &&
    isFilled(cd.nationality) &&
    isFilled(cd.email) &&
    isFilled(cd.mobilePhone?.number)
  );
}

/**
 * Check whether a single business-flow step is complete based on signal
 * fields in the application formData.
 */
export function isBusinessStepComplete(
  step: OnboardingStep,
  formData: FormData
): boolean {
  switch (step) {
    case OnboardingStep.Type:
      return isBusinessTypeComplete(formData);
    case OnboardingStep.CompanyDetails:
      return isBusinessCompanyDetailsComplete(formData);
    case OnboardingStep.CompanyAddress:
      return isBusinessCompanyAddressComplete(formData);
    case OnboardingStep.ContactDetails:
      return isBusinessContactDetailsComplete(formData);
    case OnboardingStep.AccountUse:
      return isAccountUseComplete(formData);
    case OnboardingStep.IncomingPayments:
      return isIncomingPaymentsComplete(formData);
    case OnboardingStep.OutgoingPayments:
      return isOutgoingPaymentsComplete(formData);
    case OnboardingStep.Terms:
      return Boolean(formData?.consents?.privacyAccepted);
    case OnboardingStep.Review:
      return BUSINESS_STEP_ORDER.slice(0, -1).every((s) =>
        isBusinessStepComplete(s, formData)
      );
    default:
      return false;
  }
}

// ---------------------------------------------------------------------------
// computeStepStatuses
// ---------------------------------------------------------------------------

/**
 * Compute the status metadata for every step in the given onboarding flow.
 *
 * Logic:
 *  - A step whose signal fields are populated is "completed".
 *  - The `currentUrlStep` (the page the user is on) is marked "current".
 *  - All others are "upcoming".
 *  - A step is accessible if it's completed, or if it's the first incomplete
 *    step (i.e. the user can navigate back and to the next step, but not skip).
 */
export function computeStepStatuses(
  formData: FormData,
  flowType: OnboardingType,
  currentUrlStep: OnboardingStep
): StepMeta[] {
  const stepOrder =
    flowType === "business" ? BUSINESS_STEP_ORDER : PERSONAL_STEP_ORDER;
  const isComplete =
    flowType === "business" ? isBusinessStepComplete : isPersonalStepComplete;

  let foundFirstIncomplete = false;

  return stepOrder.map((step) => {
    const complete = isComplete(step, formData);

    let isAccessible: boolean;
    if (complete) {
      isAccessible = true;
    } else if (!foundFirstIncomplete) {
      isAccessible = true;
      foundFirstIncomplete = true;
    } else {
      isAccessible = false;
    }

    let status: StepStatus;
    if (step === currentUrlStep) {
      status = "current";
    } else if (complete) {
      status = "completed";
    } else {
      status = "upcoming";
    }

    return {
      step,
      status,
      isComplete: complete,
      isAccessible,
      labelKey: STEP_LABEL_KEYS[step] ?? step,
    };
  });
}

// ---------------------------------------------------------------------------
// getResumeStep
// ---------------------------------------------------------------------------

/**
 * Return the first incomplete step. If all data steps are complete, returns
 * the Review step.
 */
export function getResumeStep(
  formData: FormData,
  flowType: OnboardingType
): OnboardingStep {
  const stepOrder =
    flowType === "business" ? BUSINESS_STEP_ORDER : PERSONAL_STEP_ORDER;
  const isComplete =
    flowType === "business" ? isBusinessStepComplete : isPersonalStepComplete;

  for (const step of stepOrder) {
    if (!isComplete(step, formData)) {
      return step;
    }
  }

  // All complete — return Review (last step in order)
  return stepOrder[stepOrder.length - 1]!;
}

// ---------------------------------------------------------------------------
// getIncompleteSteps
// ---------------------------------------------------------------------------

/**
 * Every data step still missing required answers, in flow order.
 *
 * Review is excluded because it is the step doing the asking — it is "complete"
 * precisely when this list is empty (mirrors the `Review` case of
 * `is{Business,Personal}StepComplete`). The Review page uses this to disable
 * submission and name the steps that need attention, so an application can no
 * longer be submitted around a step the user never finished.
 */
export function getIncompleteSteps(
  formData: FormData,
  flowType: OnboardingType
): OnboardingStep[] {
  const stepOrder =
    flowType === "business" ? BUSINESS_STEP_ORDER : PERSONAL_STEP_ORDER;
  const isComplete =
    flowType === "business" ? isBusinessStepComplete : isPersonalStepComplete;

  return stepOrder.slice(0, -1).filter((step) => !isComplete(step, formData));
}

// ---------------------------------------------------------------------------
// Full navigation order (includes terms, used for back-button routing)
// ---------------------------------------------------------------------------

const PERSONAL_NAV_ORDER: OnboardingStep[] = [
  OnboardingStep.ContactDetails,
  OnboardingStep.HomeAddress,
  OnboardingStep.AccountUse,
  OnboardingStep.IncomingPayments,
  OnboardingStep.OutgoingPayments,
  OnboardingStep.Review,
  OnboardingStep.Terms,
];

const BUSINESS_NAV_ORDER: OnboardingStep[] = [
  OnboardingStep.Type,
  OnboardingStep.CompanyDetails,
  OnboardingStep.CompanyAddress,
  OnboardingStep.ContactDetails,
  OnboardingStep.AccountUse,
  OnboardingStep.IncomingPayments,
  OnboardingStep.OutgoingPayments,
  OnboardingStep.Review,
  OnboardingStep.Terms,
];

/**
 * Given a pathname, return the route for the previous onboarding step,
 * or null if the current page is the first step or not a recognized step.
 */
export function getPreviousOnboardingRoute(pathname: string): string | null {
  const segments = pathname.split("/");
  const onboardingIndex = segments.indexOf("onboarding");
  if (onboardingIndex === -1) return null;

  const typeSegment = segments[onboardingIndex + 1];
  const stepSegment = segments[onboardingIndex + 2];

  if (!typeSegment || !stepSegment) return null;
  if (typeSegment !== "business" && typeSegment !== "personal") return null;

  const flowType = typeSegment as OnboardingType;
  const navOrder =
    flowType === "business" ? BUSINESS_NAV_ORDER : PERSONAL_NAV_ORDER;

  const currentIndex = navOrder.indexOf(stepSegment as OnboardingStep);
  if (currentIndex <= 0) return null;

  return getOnboardingStepRoute(flowType, navOrder[currentIndex - 1]!);
}
